import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface ApiEntry {
  name: string;
  title: string;
  description: string;
  version: string;
  pathCount: number;
  visibility?: {
    groups?: string[];
  };
}

interface ApiCatalogProps {
  userGroups: string[];
}

export function ApiCatalog({ userGroups }: ApiCatalogProps) {
  const [apis, setApis] = useState<ApiEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadApiSpecs().then((specs) => {
      // Filter APIs based on user's Cognito groups
      const visible = specs.filter((api) => {
        // No visibility config means visible to everyone
        if (!api.visibility?.groups || api.visibility.groups.length === 0) {
          return true;
        }
        // Show if user has any of the required groups
        return api.visibility.groups.some((g) => userGroups.includes(g));
      });
      setApis(visible);
      setLoading(false);
    });
  }, [userGroups]);

  if (loading) {
    return <div className="loading">Loading API catalog...</div>;
  }

  return (
    <div className="api-catalog">
      <h2>API Catalog</h2>
      <p className="subtitle">
        Browse available APIs. Each API is documented with a full OpenAPI specification.
      </p>

      <div className="api-list">
        {apis.map((api) => (
          <Link to={`/apis/${api.name}`} key={api.name} className="api-card">
            <div className="api-card-header">
              <h3>{api.title}</h3>
              <span className="version">v{api.version}</span>
            </div>
            <p>{api.description}</p>
            <div className="api-card-footer">
              <span>{api.pathCount} endpoints</span>
            </div>
          </Link>
        ))}
      </div>

      {apis.length === 0 && (
        <div className="empty-state">
          <p>No APIs configured yet. Add OpenAPI specs to the <code>/specs</code> directory.</p>
        </div>
      )}

      <style>{`
        .api-catalog h2 { margin-bottom: 0.5rem; }
        .subtitle { color: var(--color-text-muted); margin-bottom: 2rem; }
        .api-list { display: grid; gap: 1rem; }
        .api-card { display: block; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; padding: 1.5rem; text-decoration: none; color: inherit; transition: border-color 0.2s, box-shadow 0.2s; }
        .api-card:hover { border-color: var(--color-primary); box-shadow: 0 2px 8px rgba(0,115,187,0.1); text-decoration: none; }
        .api-card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
        .api-card-header h3 { color: var(--color-primary); }
        .version { background: var(--color-bg); padding: 0.25rem 0.5rem; border-radius: 4px; font-size: 0.75rem; font-weight: 500; }
        .api-card p { color: var(--color-text-muted); font-size: 0.9rem; margin-bottom: 1rem; }
        .api-card-footer { font-size: 0.8rem; color: var(--color-text-muted); }
        .empty-state { text-align: center; padding: 3rem; color: var(--color-text-muted); }
        .loading { text-align: center; padding: 3rem; }
      `}</style>
    </div>
  );
}

async function loadApiSpecs(): Promise<ApiEntry[]> {
  try {
    // In production, these would be served from S3 or an API endpoint
    // For now, we load the spec manifest
    const response = await fetch("/api-specs/manifest.json");
    if (!response.ok) {
      // Fallback: return empty if manifest not available
      return [];
    }
    return response.json();
  } catch {
    return [];
  }
}
